import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const RBACContext = createContext(null);

export const RBACProvider = ({ children }) => {
  const [navigation, setNavigation] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  
  const { token, user } = useAuth();

  const fetchNavigation = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setNavigation([]);
      setPermissions({});
      setUserRole(null);
      return;
    }

    // CRITICAL: flip loading BACK to true on every re-fetch so the
    // sidebar shows its spinner state instead of leaking a stale/empty
    // snapshot while the API call is in flight. Previously loading
    // stayed at whatever it was (usually false after the initial
    // no-token call), and login-triggered fetches produced a
    // 500ms-to-5s "flash of all menus" on dev/prod (invisible on
    // local because the API is <50ms away).
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/me/navigation?token=${token}`);
      if (response.data.success) {
        setNavigation(response.data.navigation || []);
        setUserRole(response.data.role);
        
        // Build permissions map from navigation
        const permsMap = {};
        (response.data.navigation || []).forEach(menu => {
          (menu.sub_menus || []).forEach(submenu => {
            permsMap[submenu.sub_menu_code] = submenu.allowed_actions || [];
          });
        });
        setPermissions(permsMap);
      }
    } catch (error) {
      console.error('Failed to fetch navigation:', error);
      // On error, don't block the app - just set empty permissions
      setNavigation([]);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNavigation();
  }, [fetchNavigation, user]); // Also re-fetch when user changes

  // Check if user has permission for a specific action
  const hasPermission = useCallback((permissionCode, action = 'view') => {
    // Super admin and account owner have all permissions
    if (['super_admin', 'account_owner'].includes(userRole)) {
      return true;
    }
    
    const actions = permissions[permissionCode];
    if (!actions) return false;
    return actions.includes(action);
  }, [permissions, userRole]);

  // Check if user can access a route
  const canAccessRoute = useCallback((routePath) => {
    // Super admin and account owner can access everything
    if (['super_admin', 'account_owner', 'admin'].includes(userRole)) {
      return true;
    }

    // Find submenu with this route
    for (const menu of navigation) {
      for (const submenu of menu.sub_menus || []) {
        if (submenu.route_path === routePath) {
          return true; // If in navigation, user can access
        }
      }
    }
    
    // Some routes should always be accessible
    const publicRoutes = ['/profile', '/settings', '/dashboard'];
    if (publicRoutes.includes(routePath)) {
      return true;
    }

    return false;
  }, [navigation, userRole]);

  // Get allowed actions for a permission
  const getAllowedActions = useCallback((permissionCode) => {
    if (['super_admin', 'account_owner'].includes(userRole)) {
      return ['view', 'create', 'edit', 'delete', 'export', 'import', 'approve'];
    }
    return permissions[permissionCode] || [];
  }, [permissions, userRole]);

  // Refresh navigation (e.g., after role change)
  const refreshNavigation = useCallback(() => {
    setLoading(true);
    fetchNavigation();
  }, [fetchNavigation]);

  const value = {
    navigation,
    permissions,
    userRole,
    loading,
    hasPermission,
    canAccessRoute,
    getAllowedActions,
    refreshNavigation,
    // Helper booleans
    isAdmin: ['super_admin', 'account_owner', 'admin', 'owner'].includes(userRole),
    isSuperAdmin: userRole === 'super_admin',
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within a RBACProvider');
  }
  return context;
};

// HOC for permission-protected components
export const withPermission = (WrappedComponent, permissionCode, requiredAction = 'view') => {
  return function PermissionProtectedComponent(props) {
    const { hasPermission, loading } = useRBAC();
    
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }
    
    if (!hasPermission(permissionCode, requiredAction)) {
      return (
        <div className="flex items-center justify-center h-64 text-slate-500">
          <div className="text-center">
            <div className="text-4xl mb-2">🔒</div>
            <p className="font-medium">Access Denied</p>
            <p className="text-sm">You don't have permission to view this page</p>
          </div>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};

// UI Component helpers
export const PermissionGate = ({ permission, action = 'view', children, fallback = null }) => {
  const { hasPermission } = useRBAC();
  
  if (hasPermission(permission, action)) {
    return children;
  }
  
  return fallback;
};

// Action button that hides/disables based on permission
export const PermissionButton = ({ 
  permission, 
  action, 
  children, 
  hideIfNoPermission = false,
  ...props 
}) => {
  const { hasPermission } = useRBAC();
  const allowed = hasPermission(permission, action);
  
  if (!allowed && hideIfNoPermission) {
    return null;
  }
  
  return (
    <button {...props} disabled={!allowed || props.disabled}>
      {children}
    </button>
  );
};

export default RBACContext;
